import { ReactNode, useState } from "react";
import { parseISO } from "date-fns";
import { SWRConfig } from "swr";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Skeleton } from "~/components/ui/skeleton";
import { StatsCard } from "~/components/ui/stats-card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "~/components/ui/toggle-group";
import { useIsDarkMode } from "~/hooks/useIsDarkMode";
import { useProjectContext } from "~/pages/manifold-nextgen/components/projects/useProjectContext";
import { costLocalStorageProvider } from "~/utils/costSwrProvider";
import { MonthlyCostChart } from "./MonthlyCostChart";
import { costTypeLabel, type ProjectCostSummary, summarizeMonthlyCosts } from "./costSummary";
import { costTypeColor, formatMonth } from "./theme";
import { useProjectMonthlyCosts } from "./useProjectMonthlyCosts";

const PERIODS = [
  { months: 1, label: "This month" },
  { months: 3, label: "3 months" },
  { months: 6, label: "6 months" },
  { months: 12, label: "12 months" },
];
const DEFAULT_PERIOD_MONTHS = 6;

const Panel = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="rounded-lg border border-border bg-card">
    <h2 className="border-b border-border px-4 py-3 text-sm font-semibold text-foreground">
      {title}
    </h2>
    <div className="p-4">{children}</div>
  </section>
);

const CostDashboard = ({ summary }: { summary: ProjectCostSummary }) => {
  const mode = useIsDarkMode() ? "dark" : "light";
  // summary.currency is set whenever there is any cost to show.
  const currencyFormat = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: summary.currency ?? "USD",
  });
  const formatCost = (amount: number) => currencyFormat.format(amount);
  const share = (amount: number) =>
    `${(summary.total > 0 ? (amount / summary.total) * 100 : 0).toFixed(1)}%`;

  const monthCount = summary.months.length;
  const currentMonth = summary.months[monthCount - 1];
  const [largest] = summary.byType;
  const slotOf = (costType: string) => summary.costTypes.indexOf(costType);

  return (
    <>
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatsCard
          className="border"
          title={formatCost(summary.total)}
          description={monthCount === 1 ? "Total this month" : `Total, last ${monthCount} months`}
        />
        <StatsCard
          className="border"
          title={formatCost(currentMonth.total)}
          description={`${formatMonth(currentMonth.month)} to date`}
        />
        <StatsCard
          className="border"
          title={formatCost(summary.total / monthCount)}
          description="Average per month"
        />
        <StatsCard
          className="border"
          title={costTypeLabel(largest.costType)}
          description={`Largest cost type, ${share(largest.cost)} of spend`}
        />
      </div>

      <Panel title="Monthly spend">
        <MonthlyCostChart
          months={summary.months}
          costTypes={summary.costTypes}
          formatCost={formatCost}
        />
      </Panel>

      <Panel title="Cost by type">
        <Table aria-label="Cost by type">
          <TableHeader>
            <TableRow>
              <TableHead>Cost type</TableHead>
              <TableHead className="text-right">Cost</TableHead>
              <TableHead className="w-1/3">Share</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {summary.byType.map(({ costType, cost }) => (
              <TableRow key={costType}>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <span
                      aria-hidden
                      className="size-2.5 shrink-0 rounded-sm"
                      style={{ backgroundColor: costTypeColor(slotOf(costType), mode) }}
                    />
                    {costTypeLabel(costType)}
                  </span>
                </TableCell>
                <TableCell className="text-right tabular-nums">{formatCost(cost)}</TableCell>
                <TableCell>
                  <span className="flex items-center gap-2">
                    <span aria-hidden className="h-2 flex-1 rounded-sm bg-muted">
                      <span
                        className="block h-full rounded-sm"
                        style={{
                          width: share(cost),
                          backgroundColor: costTypeColor(slotOf(costType), mode),
                        }}
                      />
                    </span>
                    <span className="w-12 text-right text-xs tabular-nums text-muted-foreground">
                      {share(cost)}
                    </span>
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>

      <Panel title="Spend by month">
        <Table aria-label="Spend by month">
          <TableHeader>
            <TableRow>
              <TableHead>Month</TableHead>
              {summary.costTypes.map((costType) => (
                <TableHead key={costType} className="text-right">
                  {costTypeLabel(costType)}
                </TableHead>
              ))}
              <TableHead className="text-right">Total</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...summary.months].reverse().map(({ month, total, byType }) => (
              <TableRow key={month}>
                <TableCell>{formatMonth(month)}</TableCell>
                {summary.costTypes.map((costType) => (
                  <TableCell key={costType} className="text-right tabular-nums">
                    {formatCost(byType[costType] ?? 0)}
                  </TableCell>
                ))}
                <TableCell className="text-right font-medium tabular-nums">
                  {formatCost(total)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Panel>

      {summary.latestCostDate && (
        <p className="text-xs text-muted-foreground">
          Cost data through{" "}
          {parseISO(summary.latestCostDate).toLocaleDateString("en-US", { dateStyle: "medium" })}.
        </p>
      )}
    </>
  );
};

const LoadingDashboard = () => (
  <div className="flex flex-col gap-6" aria-busy="true">
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {PERIODS.map(({ months }) => (
        <Skeleton key={months} className="h-20 rounded-lg" />
      ))}
    </div>
    <Skeleton className="h-80 rounded-lg" />
  </div>
);

const ProjectCostsContent = () => {
  const { project, isOwner } = useProjectContext();
  const [periodMonths, setPeriodMonths] = useState(DEFAULT_PERIOD_MONTHS);
  const { data, error } = useProjectMonthlyCosts(isOwner ? project.id : null, periodMonths);

  const renderBody = () => {
    if (!isOwner) {
      return (
        <p className="py-12 text-center text-sm text-muted-foreground">
          Only project owners can view costs.
        </p>
      );
    }
    if (error) {
      return (
        <Alert variant="destructive">
          <AlertDescription>{"Couldn't load project costs."}</AlertDescription>
        </Alert>
      );
    }
    if (!data) {
      return <LoadingDashboard />;
    }
    const summary = summarizeMonthlyCosts(data);
    if (summary.byType.length === 0) {
      return (
        <p className="rounded-lg border border-border bg-card py-12 text-center text-sm text-muted-foreground">
          No costs recorded for this project in this period.
        </p>
      );
    }
    return <CostDashboard summary={summary} />;
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="mx-auto flex max-w-5xl flex-col gap-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-foreground">Costs</h1>
            <p className="text-sm text-muted-foreground">
              Spend attributed to this project: compute environments, workflows, AI agents, and
              storage.
            </p>
          </div>
          {isOwner && (
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              aria-label="Period"
              value={String(periodMonths)}
              onValueChange={(value) => value && setPeriodMonths(Number(value))}
            >
              {PERIODS.map(({ months, label }) => (
                <ToggleGroupItem key={months} value={String(months)}>
                  {label}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          )}
        </div>
        {renderBody()}
      </div>
    </div>
  );
};

/** Owner-only cost dashboard for a project, from the cost-and-usage service. */
export const ProjectCosts = () => (
  <SWRConfig value={{ provider: costLocalStorageProvider }}>
    <ProjectCostsContent />
  </SWRConfig>
);
